import React, { useMemo, useState } from 'react';
import {
  Assignment,
  Business,
  CheckCircle,
  Schedule,
  Search,
  Visibility,
  Warning,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import { useGetAllContractsForAdminQuery } from '@/apis/extended/contractApi';
import {
  calculateContractTimeline,
  getOverdueCount,
} from '@/utils/contractTimeline';
import { Contract } from '@/types/ContractType';

interface PropertySummary {
  id: string;
  address: string;
  status: 'negotiating' | 'ratified' | 'closed' | 'cancelled';
  purchasePrice: number;
  closingDate: string;
  daysToClosing: number;
  pendingTasks: number;
  overdueItems: number;
  buyerName: string;
  sellerName: string;
  agentName: string;
  lastActivity: string;
}

const AdminPropertyList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Get real contract data from API
  const {
    data: contracts,
    isLoading,
    error,
  } = useGetAllContractsForAdminQuery();

  const normalizeContractStatus = (contract: Contract) => {
    if (contract.isRatified || contract.contractStatus === 'ratified') {
      return 'ratified' as const;
    }
    const status = contract.contractStatus || 'pending';
    if (status === 'completed' || status === 'closed') {
      return 'closed' as const;
    }
    if (status === 'cancelled' || status === 'voided') {
      return 'cancelled' as const;
    }
    return 'negotiating' as const;
  };

  // Transform contracts to PropertySummary format
  const properties: PropertySummary[] = useMemo(() => {
    if (!contracts) return [];

    return contracts.map((contract) => {
      const timelineEvents = calculateContractTimeline(contract);
      const overdueCount = getOverdueCount(timelineEvents);

      // `product.address` can be a legacy string or the newer address object.
      const productAddress = (contract as any)?.product?.address;
      const addressFromProduct =
        typeof productAddress === 'string'
          ? productAddress
          : productAddress && typeof productAddress === 'object'
          ? (productAddress.fullAddress as string) ||
            [
              productAddress.street,
              productAddress.city,
              productAddress.state,
              productAddress.zip,
            ]
              .filter(Boolean)
              .join(', ')
          : '';

      return {
        id: contract._id,
        address:
          addressFromProduct ||
          contract.buyerInfo?.street ||
          'Address not available',
        status: normalizeContractStatus(contract),
        purchasePrice:
          contract.offerSpecifics?.offerPrice ||
          contract.offerSpecifics?.purchasePrice ||
          0,
        closingDate:
          contract.offerSpecifics?.closingDate || new Date().toISOString(),
        daysToClosing: contract.offerSpecifics?.closingDate
          ? Math.ceil(
              (new Date(contract.offerSpecifics.closingDate).getTime() -
                Date.now()) /
                (1000 * 60 * 60 * 24)
            )
          : 0,
        pendingTasks: timelineEvents.filter((e) => e.status === 'pending')
          .length,
        overdueItems: overdueCount,
        buyerName:
          contract.buyer?.name || contract.buyerInfo?.name || 'Not specified',
        sellerName: contract.user?.name || 'Not specified',
        agentName: 'Property Agent', // TODO: Get actual agent name
        lastActivity: contract.updatedAt || contract.createdAt,
      };
    });
  }, [contracts]);

  const statCards = useMemo(() => {
    const totalProperties = properties.length;
    const negotiatingCount = properties.filter(
      (p) => p.status === 'negotiating'
    ).length;
    const ratifiedCount = properties.filter(
      (p) => p.status === 'ratified'
    ).length;
    const totalOverdue = properties.reduce((sum, p) => sum + p.overdueItems, 0);

    return [
      {
        key: 'total',
        title: 'Total Properties',
        value: totalProperties,
        gradient: `linear-gradient(145deg, ${alpha(
          theme.palette.primary.main,
          0.11
        )} 0%, ${alpha(theme.palette.primary.light, 0.45)} 50%, ${alpha(
          theme.palette.primary.dark,
          0.08
        )} 100%)`,
        border: alpha(theme.palette.primary.main, 0.2),
        valueColor: theme.palette.primary.main,
      },
      {
        key: 'negotiating',
        title: 'Negotiating',
        value: negotiatingCount,
        gradient: `linear-gradient(145deg, ${alpha(
          '#f59e0b',
          0.12
        )} 0%, ${alpha('#fbbf24', 0.2)} 55%, ${alpha('#d97706', 0.08)} 100%)`,
        border: alpha('#d97706', 0.28),
        valueColor: theme.palette.warning.main,
      },
      {
        key: 'ratified',
        title: 'Ratified',
        value: ratifiedCount,
        gradient: `linear-gradient(145deg, ${alpha(
          theme.palette.info.main,
          0.1
        )} 0%, ${alpha('#7dd3fc', 0.25)} 52%, ${alpha(
          theme.palette.info.dark,
          0.08
        )} 100%)`,
        border: alpha(theme.palette.info.main, 0.25),
        valueColor: theme.palette.info.main,
      },
      {
        key: 'overdue',
        title: 'Overdue Items',
        value: totalOverdue,
        gradient: `linear-gradient(145deg, ${alpha(
          theme.palette.error.main,
          0.1
        )} 0%, ${alpha('#fca5a5', 0.22)} 50%, ${alpha(
          theme.palette.error.dark,
          0.08
        )} 100%)`,
        border: alpha(theme.palette.error.main, 0.28),
        valueColor: theme.palette.error.main,
      },
    ];
  }, [theme, properties]);

  const filteredProperties = properties.filter((property) => {
    const matchesSearch =
      property.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.buyerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.sellerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.agentName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' || property.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'negotiating':
        return 'warning';
      case 'ratified':
        return 'info';
      case 'closed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'negotiating':
        return <Schedule />;
      case 'ratified':
        return <CheckCircle />;
      case 'closed':
        return <CheckCircle />;
      case 'cancelled':
        return <Warning />;
      default:
        return <Business />;
    }
  };

  const handleViewProperty = (propertyId: string) => {
    navigate(`/admin/property/${propertyId}`);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ color: 'text.secondary', mt: 2 }}>
          Loading properties...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load properties. Please try again later.
        </Alert>
      </Box>
    );
  }

  const panelPaperSx = {
    bgcolor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.greyShades.border1}`,
    borderRadius: 2,
    boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.06)}`,
    overflow: 'hidden' as const,
    color: 'text.primary',
  };

  const searchFieldSx = {
    '& .MuiOutlinedInput-root': {
      color: theme.palette.text.primary,
      bgcolor: theme.palette.background.paper,
      borderRadius: 2,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.greyShades.border1,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.text.primary, 0.22),
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statCards.map((stat) => (
          <Grid key={stat.key} item xs={12} sm={6} md={3}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${stat.border}`,
                background: stat.gradient,
                boxShadow: `0 4px 20px ${alpha(
                  theme.palette.common.black,
                  0.06
                )}`,
                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                '@media (hover: hover)': {
                  '&:hover': {
                    boxShadow: `0 8px 28px ${alpha(
                      theme.palette.common.black,
                      0.09
                    )}`,
                    transform: 'translateY(-2px)',
                  },
                },
              }}
            >
              <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    mb: 0.75,
                  }}
                >
                  {stat.title}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    color: stat.valueColor,
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                    lineHeight: 1.2,
                  }}
                >
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Search and Filters */}
      <Card elevation={0} sx={{ ...panelPaperSx, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search properties, buyers, sellers, or agents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'text.secondary', fontSize: 22 }} />
                    </InputAdornment>
                  ),
                }}
                sx={searchFieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label="All"
                  onClick={() => setStatusFilter('all')}
                  color={statusFilter === 'all' ? 'primary' : 'default'}
                  variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                />
                <Chip
                  label="Negotiating"
                  onClick={() => setStatusFilter('negotiating')}
                  color={statusFilter === 'negotiating' ? 'warning' : 'default'}
                  variant={
                    statusFilter === 'negotiating' ? 'filled' : 'outlined'
                  }
                />
                <Chip
                  label="Ratified"
                  onClick={() => setStatusFilter('ratified')}
                  color={statusFilter === 'ratified' ? 'info' : 'default'}
                  variant={statusFilter === 'ratified' ? 'filled' : 'outlined'}
                />
                <Chip
                  label="Closed"
                  onClick={() => setStatusFilter('closed')}
                  color={statusFilter === 'closed' ? 'success' : 'default'}
                  variant={statusFilter === 'closed' ? 'filled' : 'outlined'}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Properties List */}
      <Card elevation={0} sx={panelPaperSx}>
        <CardHeader
          titleTypographyProps={{
            variant: 'h6',
            sx: { color: 'text.primary', fontWeight: 600 },
          }}
          title={`Properties (${filteredProperties.length})`}
        />
        <CardContent>
          {filteredProperties.length === 0 ? (
            <Alert severity="info">
              No properties found matching your search criteria.
            </Alert>
          ) : (
            <List>
              {filteredProperties.map((property, index) => (
                <React.Fragment key={property.id}>
                  <ListItem
                    sx={{
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <ListItemText
                      component="div"
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            component="div"
                            sx={{ color: 'text.primary', fontWeight: 600 }}
                          >
                            {property.address}
                          </Typography>
                          <Chip
                            label={property.status}
                            color={getStatusColor(property.status)}
                            icon={getStatusIcon(property.status)}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            component="div"
                            sx={{
                              color: 'text.secondary',
                              mb: 1,
                            }}
                          >
                            Purchase Price: $
                            {property.purchasePrice.toLocaleString()} • Closing:{' '}
                            {new Date(
                              property.closingDate
                            ).toLocaleDateString()}{' '}
                            • Days to Close: {property.daysToClosing}
                          </Typography>
                          <Box
                            sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
                          >
                            <Chip
                              label={`Buyer: ${property.buyerName}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                color: 'text.secondary',
                                borderColor: theme.palette.greyShades.border1,
                              }}
                            />
                            <Chip
                              label={`Seller: ${property.sellerName}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                color: 'text.secondary',
                                borderColor: theme.palette.greyShades.border1,
                              }}
                            />
                            <Chip
                              label={`Agent: ${property.agentName}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                color: 'text.secondary',
                                borderColor: theme.palette.greyShades.border1,
                              }}
                            />
                            {property.pendingTasks > 0 && (
                              <Chip
                                label={`${property.pendingTasks} Tasks`}
                                size="small"
                                color="warning"
                                icon={<Assignment />}
                              />
                            )}
                            {property.overdueItems > 0 && (
                              <Chip
                                label={`${property.overdueItems} Overdue`}
                                size="small"
                                color="error"
                                icon={<Warning />}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleViewProperty(property.id)}
                        sx={{ fontWeight: 600, borderRadius: 2 }}
                      >
                        View Details
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < filteredProperties.length - 1 && (
                    <Divider sx={{ borderColor: 'divider' }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminPropertyList;
